import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import {
  createOutline, textOutline, calendarOutline, calendarNumberOutline, textSharp, calculatorOutline,
  checkboxOutline, chevronDownCircleOutline, radioButtonOnOutline,
} from "ionicons/icons";

// Uploaded documents for signature: the field types a sender places on their
// PDF, each signer role's colour, and the stamping that turns the PDF plus
// everyone's values into the final signed copy.
//
// A field: { id, type, role, page, x, y, w, h, label, required, options?,
// value? }. Position and size are fractions of the page (top-left origin),
// so a field sits in the same place however large the page is drawn. role
// is a signer role's id, or "sender" for something the sender fills in.

export const FIELD_TYPES = [
  { type: "signature", label: "Signature", icon: createOutline, w: 0.26, h: 0.06 },
  { type: "initials", label: "Initials", icon: textSharp, w: 0.1, h: 0.05 },
  { type: "date", label: "Date signed", icon: calendarOutline, w: 0.18, h: 0.032 },
  { type: "pickdate", label: "Date", icon: calendarNumberOutline, w: 0.18, h: 0.032 },
  { type: "text", label: "Text", icon: textOutline, w: 0.26, h: 0.032 },
  { type: "number", label: "Number", icon: calculatorOutline, w: 0.14, h: 0.032 },
  { type: "checkbox", label: "Checkbox", icon: checkboxOutline, w: 0.028, h: 0.022 },
  { type: "dropdown", label: "Dropdown", icon: chevronDownCircleOutline, w: 0.22, h: 0.032 },
  { type: "radio", label: "Radio", icon: radioButtonOnOutline, w: 0.2, h: 0.075 },
];
export const fieldMeta = (type) => FIELD_TYPES.find((f) => f.type === type) || FIELD_TYPES[3];

// the types a sender may fill in themselves before sending: their own
// signature and initials (an image: a saved Branding signature, a drawing or
// an upload), and plain values
export const SENDER_TYPES = ["signature", "initials", "text", "number", "pickdate", "checkbox"];
// the sender's own signature and initials boxes still empty
export const senderUnsigned = (fields) =>
  (fields || []).filter((f) => f.role === "sender" && (f.type === "signature" || f.type === "initials") && !f.value);

const ROLE_COLORS = ["#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#65a30d", "#c026d3", "#4b5563"];
export const SENDER_COLOR = "#334155";
export const roleColor = (roles, roleId) => {
  if (roleId === "sender") return SENDER_COLOR;
  const i = (roles || []).findIndex((r) => r.id === roleId);
  return ROLE_COLORS[(i < 0 ? 0 : i) % ROLE_COLORS.length];
};
export const roleLabel = (roles, roleId) => (roleId === "sender" ? "You (prefilled)" : ((roles || []).find((r) => r.id === roleId) || {}).label || "Signer");

export const newId = () => Math.random().toString(36).slice(2, 12);

export const newField = (type, role, page, cx, cy) => {
  const m = fieldMeta(type);
  const x = Math.min(1 - m.w, Math.max(0, cx - m.w / 2));
  const y = Math.min(1 - m.h, Math.max(0, cy - m.h / 2));
  const f = { id: newId(), type, role, page, x, y, w: m.w, h: m.h, label: m.type === "date" ? "Date signed" : "", required: type !== "checkbox" };
  if (type === "dropdown" || type === "radio") f.options = ["Option 1", "Option 2"];
  return f;
};

// How a date field writes its date, chosen per field in the editor
export const DATE_FORMATS = [
  { key: "long", label: "September 24, 2026" },
  { key: "short", label: "Sep 24, 2026" },
  { key: "mdy", label: "09/24/2026" },
  { key: "dmy", label: "24/09/2026" },
  { key: "dmy-long", label: "24 September 2026" },
  { key: "iso", label: "2026-09-24" },
];
const pad2 = (n) => String(n).padStart(2, "0");
export function formatDate(d, key = "long") {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate();
  const month = (style) => d.toLocaleDateString("en-US", { month: style });
  switch (key) {
    case "short": return `${month("short")} ${day}, ${y}`;
    case "mdy": return `${pad2(m)}/${pad2(day)}/${y}`;
    case "dmy": return `${pad2(day)}/${pad2(m)}/${y}`;
    case "dmy-long": return `${day} ${month("long")} ${y}`;
    case "iso": return `${y}-${pad2(m)}-${pad2(day)}`;
    default: return `${month("long")} ${day}, ${y}`;
  }
}
// today, in a field's format (a Date signed field's value)
export const todayLong = (key = "long") => formatDate(new Date(), key);
// a picked date (YYYY-MM-DD) in a field's format
export const longDate = (iso, key = "long") => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return y && m && d ? formatDate(new Date(y, m - 1, d), key) : String(iso || "");
};

// what a filled value reads as on the page
export const displayValue = (f, v) => {
  if (v === undefined || v === null || v === "") return "";
  if (f.type === "checkbox") return v ? "✓" : "";
  if (f.type === "pickdate") return longDate(v, f.format);
  return String(v);
};

export const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

// The final copy: the original PDF with every value stamped where its field
// sits. Signatures and initials are drawn images; the rest is text.
export async function stampSignDoc(pdfBytes, fields, values) {
  const pdf = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const pages = pdf.getPages();
  for (const f of fields || []) {
    const page = pages[f.page];
    if (!page) continue;
    const v = f.role === "sender" ? (values?.[f.id] ?? f.value) : values?.[f.id];
    if (v === undefined || v === null || v === "" || v === false) continue;
    const { width: pw, height: ph } = page.getSize();
    const bx = f.x * pw, bw = f.w * pw, bh = f.h * ph;
    const by = ph - (f.y + f.h) * ph; // PDF origin is bottom-left
    if (f.type === "signature" || f.type === "initials") {
      try {
        const img = String(v).startsWith("data:image/png") ? await pdf.embedPng(v) : await pdf.embedJpg(v);
        const s = Math.min(bw / img.width, bh / img.height);
        const w = img.width * s, h = img.height * s;
        page.drawImage(img, { x: bx + (bw - w) / 2, y: by + (bh - h) / 2, width: w, height: h });
      } catch { /* an unreadable image leaves the box empty */ }
      continue;
    }
    if (f.type === "checkbox") {
      // a drawn tick, sized to the box
      const t = Math.min(bw, bh);
      const ox = bx + (bw - t) / 2, oy = by + (bh - t) / 2;
      const c = rgb(0.06, 0.09, 0.16), th = Math.max(1, t * 0.12);
      page.drawLine({ start: { x: ox + t * 0.15, y: oy + t * 0.5 }, end: { x: ox + t * 0.42, y: oy + t * 0.2 }, thickness: th, color: c });
      page.drawLine({ start: { x: ox + t * 0.42, y: oy + t * 0.2 }, end: { x: ox + t * 0.88, y: oy + t * 0.82 }, thickness: th, color: c });
      continue;
    }
    const text = displayValue(f, v).replace(/\s+/g, " ").trim();
    let size = Math.max(6, Math.min(12, bh * 0.7));
    while (size > 6 && font.widthOfTextAtSize(text, size) > bw - 4) size -= 0.5;
    let out = text;
    while (out.length > 1 && font.widthOfTextAtSize(out, size) > bw - 4) out = out.slice(0, -1);
    page.drawText(out, { x: bx + 2, y: by + (bh - size) / 2 + size * 0.22, size, font, color: rgb(0.06, 0.09, 0.16) });
  }
  return new Blob([await pdf.save()], { type: "application/pdf" });
}

// a signer's fields still left to fill (required ones only)
export const missingRequired = (fields, roleId, values) =>
  (fields || []).filter((f) => f.role === roleId && f.required && f.type !== "date"
    && (values?.[f.id] === undefined || values?.[f.id] === "" || values?.[f.id] === null || values?.[f.id] === false));
